import React, { useRef, useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { CompanyAddFormViewModel } from '../../view-model/professional/company/company-add-form-view-model';
import { CompanyEditFormViewModel } from '../../view-model/professional/company/company-edit-form-view-model';
import { compressImage } from '../../utils/image-compression';

interface CompanyLogoUploadProps {
  viewModel: CompanyAddFormViewModel | CompanyEditFormViewModel;
}

/**
 * Composant réutilisable pour gérer l'upload et l'affichage du logo de l'entreprise
 * Utilisé dans les formulaires d'ajout et d'édition
 *
 * @param viewModel - Instance du ViewModel (Add ou Edit)
 */
const CompanyLogoUpload = observer(({ viewModel }: CompanyLogoUploadProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressing(true);

      // Compresser l'image
      const compressedFile = await compressImage(file);

      // Créer un nouvel événement avec le fichier compressé
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(compressedFile);

      const newEvent = {
        ...e,
        target: {
          ...e.target,
          files: dataTransfer.files,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      viewModel.handleLogoChange(newEvent);
    } catch (error) {
      console.error('Erreur lors de la compression:', error);
      // En cas d'erreur, utiliser le fichier original
      viewModel.handleLogoChange(e);
    } finally {
      setIsCompressing(false);
    }
  }, [viewModel]);

  const handleRemoveLogo = useCallback(() => {
    viewModel.removeLogo();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [viewModel]);

  // Déterminer l'URL à afficher (prévisualisation ou logo existant)
  const displayUrl = viewModel.logoPreviewUrl ||
    ('companyLogo' in viewModel.formData ? viewModel.formData.companyLogo : null);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {t('company.form.logo', 'Logo de l\'entreprise')}
      </label>

      <div className="flex items-center space-x-4">
        {/* Prévisualisation du logo */}
        <div
          className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 hover:border-gray-400 transition-colors overflow-hidden cursor-pointer"
          onClick={!isCompressing ? handleClickUpload : undefined}
        >
          {isCompressing ? (
            <div className="flex flex-col items-center">
              <Loader2 size={24} className="text-primary animate-spin" />
              <span className="text-xs mt-1 text-primary">
                {t('company.form.compressing', 'Compression...')}
              </span>
            </div>
          ) : displayUrl ? (
            <img
              src={displayUrl}
              alt="Logo"
              className="w-full h-full object-cover"
            />
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xs mt-1">
                {t('company.form.uploadLogo', 'Ajouter un logo')}
              </span>
            </>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            id="companyLogoInput"
            accept="image/png, image/jpeg, image/jpg, image/webp"
            onChange={handleFileChange}
            className="hidden"
          />

          <label
            htmlFor="companyLogoInput"
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-center"
          >
            {displayUrl
              ? t('company.form.changeLogo', 'Changer le logo')
              : t('company.form.selectLogo', 'Sélectionner un logo')
            }
          </label>

          {displayUrl && (
            <button
              type="button"
              onClick={handleRemoveLogo}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-xs"
            >
              {t('common.remove', 'Supprimer')}
            </button>
          )}
        </div>
      </div>

      <p className="mt-1 text-xs text-gray-500">
        {t('company.form.logoHelp', 'Format recommandé: JPG, PNG, WEBP. Optimisé automatiquement')}
      </p>

      {/* Affichage de l'erreur si présente */}
      {viewModel.errors.logo && (
        <p className="text-xs text-red-500 mt-1">{viewModel.errors.logo}</p>
      )}
    </div>
  );
});

export default CompanyLogoUpload;
